import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, Subject, filter, map, BehaviorSubject, take, takeUntil } from 'rxjs';

import { EffectStatusData, PackingStatus, QuotationReadyData, WsMessage, WsMessageType } from '~shared/types/websockets.types';
import { MaintenanceStatus } from '~shared/types/maintenance.types';

import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class WebsocketService {
	private socket?: WebSocket;
	private url?: string;
	private token?: string;

	private messages$ = new Subject<any>();
	private isConnected = false;
	private manualClose = false;
	private onInactivityClose = false;
	private pendingReconnect = false;
	private reconnectAttempts = 0;
	private readonly connectionState$ = new BehaviorSubject<'connected' | 'disconnected' | 'connecting' | 'failed'>('disconnected');

	private readonly inactivityLimit = 30 * 60 * 1000;
	private inactivityTimer?: ReturnType<typeof setTimeout>;

	private readonly pingInterval = 30_000;
	private readonly pongTimeout = 10_000;
	private pingTimer?: ReturnType<typeof setInterval>;
	private pongTimer?: ReturnType<typeof setTimeout>;
	private lastMessageAt = 0;

	private readonly minReconnectDelay = 5000;
	private readonly maxReconnectDelay = 60000;
	private readonly maxReconnectAttempts = 7;
	private readonly firstReconnectAttempt$ = new Subject<void>();
	private reconnectTimer?: ReturnType<typeof setTimeout>;

	/**
	 * A socket only ever exists in the browser. The service is a root singleton, so it
	 * is also constructed — and torn down — during server-side rendering, where neither
	 * `document` nor (depending on the Node version) the `WebSocket` global exists.
	 */
	private readonly isBrowser: boolean;

	constructor(private zone: NgZone, @Inject(PLATFORM_ID) platformId: object) {
		this.isBrowser = isPlatformBrowser(platformId);

		if (this.isBrowser) {
			document.addEventListener('visibilitychange', () => {
				if (document.visibilityState === 'visible' && this.pendingReconnect && !this.manualClose) {
					console.log(`[${this.ts()}] WS tab visible — reconnecting (deferred after server close)`);
					this.pendingReconnect = false;
					this.reconnect(this.token);
				}
			});
		}
	}

	/**
	 * Reading `WebSocket.OPEN` touches the global, which optional chaining on `this.socket`
	 * does not guard: `this.socket?.readyState === WebSocket.OPEN` still evaluates the right
	 * operand when there is no socket. Route every readyState check through these so the
	 * global is only ever dereferenced once a socket exists.
	 */
	private get socketIsOpen(): boolean {
		return !!this.socket && this.socket.readyState === WebSocket.OPEN;
	}

	private get socketIsConnecting(): boolean {
		return !!this.socket && this.socket.readyState === WebSocket.CONNECTING;
	}

	private ts(): string {
		return new Date().toLocaleTimeString();
	}

	connect(token: string): void {
		if (!this.isBrowser) {
			return;
		}

		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = undefined;
		}

		if (this.isConnected && this.socketIsOpen && (this.token === token || !this.token)) {
			return;
		}
		if (this.isConnected && this.socketIsOpen && (this.token !== token && !!this.token)) {
			this.socket.close();
		}

		this.token = token;
		this.manualClose = false;
		this.onInactivityClose = false;

		this.zone.runOutsideAngular(() => {
			try {
				this.connectionState$.next('connecting');
				this.url = `${environment.wsUrl}?token=${encodeURIComponent(token)}`;
				this.socket = new WebSocket(this.url);
				this.addListeners();
			} catch (error) {
				console.error(`[${this.ts()}] WS connection error:`, error);
				this.connectionState$.next('failed');
			}
		});
	}

	addListeners(): void {
		this.socket.onopen = () => {
			console.log(`[${this.ts()}] WS connected`);
			this.send('Hello');
			this.isConnected = true;
			this.lastMessageAt = Date.now();
			this.resetInactivityTimer();
			this.startPing();
			this.connectionState$.next('connected');
			this.reconnectAttempts = 0;
		};

		this.socket.onmessage = (event) => {
			this.lastMessageAt = Date.now();
			this.clearPongTimeout();
			try {
				const message: WsMessage = JSON.parse(event.data);
				this.zone.run(() => this.messages$.next(message));
			} catch {
				this.zone.run(() => this.messages$.next(event.data));
			}
		};

		this.socket.onclose = () => {
			const reason = this.onInactivityClose ? 'inactivity' : this.manualClose ? 'manual' : 'server';
			console.log(`[${this.ts()}] WS closed (${reason})`);
			this.isConnected = false;
			this.stopPing();
			this.stopInactivityTimer();
			this.connectionState$.next('disconnected');
			if (!this.manualClose) {
				this.reconnect(this.token);
			}
		};

		this.socket.onerror = () => {
			// Browser will surface a native console message for handshake/network failures;
			// onclose will fire immediately after with the lifecycle log + reconnect trigger.
			this.socket?.close();
		};
	}

	close(manualClose = true): void {
		this.manualClose = manualClose;
		this.isConnected = false;

		// A deliberate non-inactivity close (logout, route change) cancels all pending reconnect state
		if (manualClose && !this.onInactivityClose) {
			this.pendingReconnect = false;
		}

		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = undefined;
		}

		if (this.socketIsOpen || this.socketIsConnecting) {
			this.socket.close();
		} else {
			this.stopInactivityTimer();
			this.connectionState$.next('disconnected');
		}
	}

	private reconnect(token: string): void {
		if (document.visibilityState === 'hidden') {
			console.log(`[${this.ts()}] WS tab hidden — deferring reconnect until visible`);
			this.pendingReconnect = true;
			return;
		}

		if (this.reconnectAttempts >= this.maxReconnectAttempts) {
			console.log(`[${this.ts()}] WS reconnect gave up after ${this.maxReconnectAttempts} attempts`);
			this.connectionState$.next('failed');
			return;
		}

		if (this.reconnectAttempts === 0) {
			this.firstReconnectAttempt$.next();
		}

		const delay = Math.min(this.minReconnectDelay * Math.pow(2, this.reconnectAttempts), this.maxReconnectDelay);
		console.log(`[${this.ts()}] WS reconnect attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts} in ${delay / 1000}s`);
		this.reconnectAttempts++;
		this.reconnectTimer = setTimeout(() => {
			this.connect(token);
		}, delay);
	}

	get onFirstReconnectAttempt$() {
		return this.firstReconnectAttempt$.asObservable();
	}

	get wsConnectionState$() {
		return this.connectionState$.asObservable();
	}

	getPartWSMessages$(): Observable<WsMessage> {
		return this.messages$.pipe(
			filter((msg) => msg.domain === WsMessageType.PART && msg.eventType.toLowerCase() === 'update'),
		);
	}

	getProjectWSMessages$(): Observable<WsMessage> {
		return this.messages$.pipe(
			filter((msg) => msg.domain === WsMessageType.PROJECT && msg.eventType.toLowerCase() === 'update'),
		);
	}

	getMaintenanceWSMessages$(): Observable<MaintenanceStatus> {
		return this.messages$.pipe(
			filter((msg) => msg.domain === WsMessageType.MAINTENANCE),
			filter((msg) => !!msg.data),
			map((msg) => msg.data as unknown as MaintenanceStatus),
		);
	}

	/** Finished-PDF pushes for the account. Account-scoped by the server, so the caller
	 * filters by `quotationId` to pick out the render it started. */
	getQuotationWSMessages$(): Observable<QuotationReadyData> {
		return this.messages$.pipe(
			filter((msg) => msg.domain === WsMessageType.QUOTATION && msg.eventType.toLowerCase() === 'update'),
			filter((msg) => !!msg.data),
			map((msg) => msg.data as unknown as QuotationReadyData),
		);
	}

	getPackingWSMessages$(projectId: string): Observable<PackingStatus> {
		return this.messages$.pipe(
			filter((msg) => msg.domain === WsMessageType.PACKING && msg.identifier?.projectId === projectId),
			filter((msg) => !!msg.data),
			map((msg) => msg.data as unknown as PackingStatus),
		);
	}

	getEffectWSMessages$(projectId: string): Observable<EffectStatusData> {
		return this.messages$.pipe(
			filter((msg) =>
				msg.domain === WsMessageType.PROJECT
				&& msg.eventType === 'STATUS_UPDATE'
				&& msg.subEventType === 'EFFECT'
				&& msg.identifier?.projectId === projectId
			),
			filter((msg) => !!msg.data),
			map((msg) => msg.data as unknown as EffectStatusData),
		);
	}

	send(message: WsMessage | string): void {
		if (this.socketIsOpen) {
			this.socket.send(JSON.stringify(message));
			return;
		}

		this.connectionState$
			.pipe(
				filter((state) => state === 'connected'),
				take(1),
				takeUntil(this.connectionState$.pipe(filter(state => state === 'failed')))
			)
			.subscribe({
				next: () => {
					if (this.socketIsOpen) {
						this.socket.send(JSON.stringify(message));
					}
				}
			});
	}

	markActivity(): void {
		if (this.isConnected) {
			this.resetInactivityTimer();
		}
		if (!this.isConnected && this.token && this.connectionState$.getValue() !== 'connecting' && this.onInactivityClose) {
			console.log(`[${this.ts()}] WS activity detected — reconnecting after inactivity`);
			this.onInactivityClose = false;
			this.connect(this.token);
		}
	}

	private resetInactivityTimer(): void {
		this.stopInactivityTimer();
		this.inactivityTimer = setTimeout(() => {
			console.log(`[${this.ts()}] WS closing due to ${this.inactivityLimit / 60_000} min inactivity`);
			this.onInactivityClose = true;
			this.close(true);
		}, this.inactivityLimit);
	}

	private stopInactivityTimer(): void {
		if (this.inactivityTimer) {
			clearTimeout(this.inactivityTimer);
			this.inactivityTimer = undefined;
		}
	}

	private startPing(): void {
		this.stopPing();
		this.pingTimer = setInterval(() => {
			if (this.socketIsOpen) {
				const silentSinceLastPing = Date.now() - this.lastMessageAt > this.pingInterval;
				if (!silentSinceLastPing) {
					return;
				}
				this.socket.send(JSON.stringify('Hello'));
				this.pongTimer = setTimeout(() => {
					console.log(`[${this.ts()}] WS stale — no response within ${this.pongTimeout / 1000}s`);
					this.manualClose = false;
					this.socket?.close();
				}, this.pongTimeout);
			}
		}, this.pingInterval);
	}

	private stopPing(): void {
		if (this.pingTimer) {
			clearInterval(this.pingTimer);
			this.pingTimer = undefined;
		}
		this.clearPongTimeout();
	}

	private clearPongTimeout(): void {
		if (this.pongTimer) {
			clearTimeout(this.pongTimer);
			this.pongTimer = undefined;
		}
	}
}
